/**
 * Coaching side panel for trainers: a side chat beside one of their own Flux Agent conversations.
 * The trainer tells the agent what it should have asked or done; the agent stages system-prompt
 * and playbook changes (it never commits from here), shown as server-computed diffs. **Accept &
 * re-run** commits them in one library commit and asks the page to re-run the coached
 * conversation's last message with the updated prompt and playbooks; **Discard** drops them.
 *
 * @module apps/web/src/components/flux-agent/FluxAgentCoachPanel
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangle, Check, GraduationCap, Loader2, Send, Trash2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FluxAgentActionCards } from '@/components/flux-agent/FluxAgentActionCards';
import { FluxAgentDiffView } from '@/components/flux-agent/FluxAgentDiffView';
import { FluxAgentMarkdown } from '@/components/flux-agent/FluxAgentMarkdown';
import { refreshAfterAgentActions } from '@/components/flux-agent/plannerRefresh';
import {
  FLUX_AGENT_VERSION_CONFLICT,
  fluxAgentApi,
  withResolvedCard,
  type CoachingProposal,
  type CoachingSession,
  type FluxAgentAction,
  type FluxAgentLiveForm,
  type FluxAgentConversation,
  type FluxAgentMessage,
} from '@/lib/fluxAgentApi';
import { cn } from '@/lib/utils';
import { readUiCache, writeUiCache } from '@/lib/uiCache';

/** UI-cache key of the last coaching session shown beside `conversationId`. */
const sessionKey = (conversationId: string) => `flux-agent:coach:${conversationId}`;

/** Inputs for {@link FluxAgentCoachPanel}. */
export interface FluxAgentCoachPanelProps {
  /** Id of the conversation being coached; the side chat is opened (or created) for it. */
  conversationId: string;
  /** Close the panel; staged proposals are kept for the next time it opens. */
  onClose: () => void;
  /**
   * Called after the proposals were committed: re-run the coached conversation's last message.
   * Resolves when the new reply has arrived (or failed; the page reports that itself).
   */
  onAccepted: () => Promise<void>;
  /** Extra classes for the panel's outer element. */
  className?: string;
}

/** Render the coaching side chat, its staged proposals, and the accept and discard actions. */
export function FluxAgentCoachPanel({
  conversationId,
  onClose,
  onAccepted,
  className,
}: FluxAgentCoachPanelProps) {
  // Reopening the panel shows the session it last showed while the server reopens it.
  const [cached] = useState(() => readUiCache<CoachingSession>(sessionKey(conversationId)));
  const [chat, setChat] = useState<FluxAgentConversation | null>(cached?.conversation ?? null);
  const [messages, setMessages] = useState<FluxAgentMessage[]>(cached?.messages ?? []);
  const [proposals, setProposals] = useState<CoachingProposal[]>(cached?.proposals ?? []);
  const [loading, setLoading] = useState(!cached);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [streamText, setStreamText] = useState('');
  const [streamActions, setStreamActions] = useState<FluxAgentAction[]>([]);
  const [streamForms, setStreamForms] = useState<FluxAgentLiveForm[]>([]);
  const [busy, setBusy] = useState<'accept' | 'discard' | null>(null);
  const end = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    const known = readUiCache<CoachingSession>(sessionKey(conversationId));
    if (known) {
      setChat(known.conversation);
      setMessages(known.messages);
      setProposals(known.proposals);
    }
    setLoading(!known);
    setError(null);
    void fluxAgentApi.openCoaching(conversationId).then(result => {
      if (cancelled) return;
      setLoading(false);
      if (!result.ok) {
        setError(result.message);
        return;
      }
      setChat(result.data.conversation);
      setMessages(result.data.messages);
      setProposals(result.data.proposals);
    });
    return () => {
      cancelled = true;
    };
  }, [conversationId]);

  // Remember what is shown; the optimistic message of a reply in flight is not stored.
  useEffect(() => {
    if (chat?.id && !sending)
      writeUiCache<CoachingSession>(sessionKey(conversationId), {
        conversation: chat,
        messages,
        proposals,
      });
  }, [chat, messages, proposals, sending, conversationId]);

  useEffect(() => {
    end.current?.scrollIntoView?.({ block: 'end' });
  }, [messages, streamText, proposals]);

  const refreshProposals = useCallback(async (chatId: string) => {
    const result = await fluxAgentApi.listProposals(chatId);
    if (result.ok) setProposals(result.data.proposals);
  }, []);

  const send = async () => {
    const text = draft.trim();
    if (!text || !chat || sending) return;
    setDraft('');
    setSending(true);
    setError(null);
    setStreamText('');
    setStreamActions([]);
    setStreamForms([]);
    const optimistic: FluxAgentMessage = {
      id: `pending-${Date.now()}`,
      role: 'user',
      content: text,
      createdAt: new Date().toISOString(),
      actions: [],
      files: [],
    };
    setMessages(current => [...current, optimistic]);
    let round = 0;
    const result = await fluxAgentApi.streamMessage(chat.id, text, [], {
      onDelta: (delta, nextRound) => {
        setStreamText(current => (nextRound === round ? current + delta : delta));
        round = nextRound;
      },
      onAction: action => setStreamActions(current => [...current, action]),
      onForm: live =>
        setStreamForms(current => [...current.filter(form => form.slot !== live.slot), live]),
    });
    setSending(false);
    setStreamText('');
    setStreamActions([]);
    setStreamForms([]);
    if (!result.ok) {
      setError(result.message);
      const reloaded = await fluxAgentApi.getConversation(chat.id);
      if (reloaded.ok) setMessages(reloaded.data.messages);
      else setMessages(current => current.filter(message => message.id !== optimistic.id));
      setDraft(current => current || text);
    } else {
      setMessages(current => [
        ...current.filter(message => message.id !== optimistic.id),
        ...result.data.messages,
      ]);
      void refreshAfterAgentActions(result.data.messages.flatMap(message => message.actions || []));
    }
    await refreshProposals(chat.id);
  };

  const accept = async () => {
    if (!chat || busy) return;
    setBusy('accept');
    setError(null);
    const result = await fluxAgentApi.acceptProposals(chat.id);
    if (!result.ok) {
      setBusy(null);
      setError(
        result.code === FLUX_AGENT_VERSION_CONFLICT
          ? 'Someone changed one of these files since it was drafted. Ask Flux Agent to redo the change against the current version.'
          : result.message
      );
      await refreshProposals(chat.id);
      return;
    }
    setProposals([]);
    await onAccepted();
    setBusy(null);
  };

  const discard = async () => {
    if (!chat || busy) return;
    setBusy('discard');
    setError(null);
    const result = await fluxAgentApi.discardProposals(chat.id);
    setBusy(null);
    if (!result.ok) setError(result.message);
    else setProposals([]);
  };

  const stale = proposals.some(proposal => proposal.stale);

  return (
    <aside
      className={cn(
        'bg-card/60 flex min-h-0 flex-col overflow-hidden rounded-xl border shadow-sm',
        className
      )}
      aria-label="Coach this conversation"
    >
      <div className="flex items-center gap-2 border-b px-4 py-2.5">
        <GraduationCap className="text-primary h-4 w-4 shrink-0" aria-hidden="true" />
        <h2 className="min-w-0 flex-1 truncate text-sm font-medium">Coach this conversation</h2>
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className="h-7 w-7"
          onClick={onClose}
          aria-label="Close coaching"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div
        className="native-scroll min-h-0 flex-1 space-y-3 overflow-y-auto px-3 py-3"
        role="log"
        aria-label="Coaching messages"
        aria-busy={loading || sending}
      >
        {loading ? (
          <p className="text-muted-foreground flex items-center gap-2 text-sm">
            <Loader2 className="h-4 w-4 animate-spin" /> Opening the coaching chat…
          </p>
        ) : (
          <>
            {messages.length === 0 && (
              <p className="text-muted-foreground text-sm">
                Tell Flux Agent what it should have asked or done in this conversation. It will
                propose changes to the playbooks or system prompt for you to review here.
              </p>
            )}
            <ol className="space-y-3">
              {messages.map(message => (
                <li
                  key={message.id}
                  className={cn('flex', message.role === 'user' ? 'justify-end' : 'justify-start')}
                >
                  {message.role === 'user' ? (
                    <p className="bg-primary text-primary-foreground max-w-[90%] whitespace-pre-wrap break-words rounded-2xl rounded-br-md px-3 py-2 text-sm">
                      <span className="sr-only">You said: </span>
                      {message.content}
                    </p>
                  ) : (
                    <div className="bg-background min-w-0 max-w-[95%] rounded-2xl rounded-tl-md border px-3 py-2">
                      <span className="sr-only">Flux Agent said: </span>
                      <FluxAgentMarkdown content={message.content} />
                      <FluxAgentActionCards
                        actions={message.actions || []}
                        onResolved={card => setMessages(current => withResolvedCard(current, card))}
                      />
                    </div>
                  )}
                </li>
              ))}
              {sending && (
                <li className="flex justify-start" data-testid="coaching-streaming-reply">
                  <div className="bg-background text-muted-foreground min-w-0 max-w-[95%] rounded-2xl rounded-tl-md border px-3 py-2 text-sm">
                    {streamText ? <FluxAgentMarkdown content={streamText} /> : 'Thinking…'}
                    <FluxAgentActionCards actions={streamActions} liveForms={streamForms} />
                  </div>
                </li>
              )}
            </ol>

            {proposals.length > 0 && (
              <section aria-label="Proposed changes" className="space-y-3 border-t pt-3">
                <h3 className="text-sm font-medium">
                  Proposed changes{' '}
                  <span className="text-muted-foreground font-normal">(not live yet)</span>
                </h3>
                {proposals.map(proposal => (
                  <article key={proposal.path} className="space-y-1.5">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <code className="text-xs font-medium">{proposal.path}</code>
                      <Badge variant="secondary" className="text-[10px]">
                        +{proposal.added} −{proposal.removed}
                      </Badge>
                      {proposal.deleted && (
                        <Badge variant="destructive" className="text-[10px]">
                          Delete
                        </Badge>
                      )}
                      {proposal.stale && (
                        <Badge variant="outline" className="gap-1 text-[10px]">
                          <AlertTriangle className="h-3 w-3" /> Changed since drafted
                        </Badge>
                      )}
                    </div>
                    <p className="text-muted-foreground text-xs">{proposal.changeNote}</p>
                    {proposal.warnings.map(warning => (
                      <p key={warning} className="text-xs text-amber-700 dark:text-amber-300">
                        {warning}
                      </p>
                    ))}
                    <FluxAgentDiffView diff={proposal.diff} label={`Changes to ${proposal.path}`} />
                  </article>
                ))}
              </section>
            )}
          </>
        )}
        <div ref={end} />
      </div>

      <div className="bg-background/60 space-y-2 border-t p-2.5">
        {error && (
          <p role="alert" className="text-destructive text-sm">
            {error}
          </p>
        )}
        {proposals.length > 0 && (
          <div className="flex gap-2">
            <Button
              type="button"
              size="sm"
              className="flex-1 gap-1.5"
              disabled={!!busy || sending || stale}
              onClick={() => void accept()}
            >
              {busy === 'accept' ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Check className="h-4 w-4" />
              )}
              Accept &amp; re-run
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="gap-1.5"
              disabled={!!busy || sending}
              onClick={() => void discard()}
            >
              {busy === 'discard' ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
              Discard
            </Button>
          </div>
        )}
        <form
          className="flex items-end gap-2"
          onSubmit={event => {
            event.preventDefault();
            void send();
          }}
        >
          <textarea
            value={draft}
            onChange={event => setDraft(event.target.value)}
            onKeyDown={event => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                void send();
              }
            }}
            rows={2}
            disabled={loading || !chat}
            placeholder="What should Flux Agent have done instead?"
            aria-label="Coaching message"
            className="bg-background focus-visible:ring-ring min-h-[2.5rem] flex-1 resize-none rounded-md border px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2"
          />
          <Button
            type="submit"
            size="icon"
            disabled={!draft.trim() || sending || loading || !chat}
            aria-label="Send coaching message"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </form>
      </div>
    </aside>
  );
}
